import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { 
  ChevronDown,
  // Essential icons only - reduced from 150+ to 50 most common
  Home, User, Settings, Search, Plus, Minus, X, Check, 
  ArrowLeft, ArrowRight, ArrowUp, ArrowDown,
  Calendar, Clock, Mail, Phone, MapPin, Heart,
  Star, Bookmark, Tag, Flag, Bell, Shield,
  Lock, Unlock, Eye, EyeOff, Edit, Trash2,
  Download, Upload, Share, Copy, Save, Printer,
  File, Folder, Image, Video, Music, Code,
  Camera, Mic, Play, Pause, Square, Volume,
  Wifi, Battery, Globe, Link, Zap, Flame,
  Sun, Moon, Cloud, Umbrella, Snowflake,
  Car, Plane, Ship, Train, Bike, Bus,
  ChefHat, Coffee, Pizza, Utensils,
  ShoppingCart, Package, Gift, CreditCard,
  Building, Hospital, School, Factory,
  Book, GraduationCap, Briefcase, Award,
  Users, UserPlus, UserMinus, UserCheck,
  MessageSquare, MessageCircle, Send, Inbox,
  BarChart3, TrendingUp, TrendingDown, BarChart,
  PieChart, Activity, Target, Layers,
  Grid, List, Filter, ArrowUpDown, RefreshCw,
  Maximize, Minimize, RotateCw, RotateCcw,
  Info, AlertCircle, AlertTriangle, CheckCircle,
  XCircle, HelpCircle, Lightbulb
} from "lucide-react";

// Define available icons with their components
const AVAILABLE_ICONS = {
  'home': Home,
  'user': User,
  'settings': Settings,
  'search': Search,
  'plus': Plus,
  'minus': Minus,
  'x': X,
  'check': Check,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'calendar': Calendar,
  'clock': Clock,
  'mail': Mail,
  'phone': Phone,
  'map-pin': MapPin,
  'heart': Heart,
  'star': Star,
  'bookmark': Bookmark,
  'tag': Tag,
  'flag': Flag,
  'bell': Bell,
  'shield': Shield,
  'lock': Lock,
  'unlock': Unlock,
  'eye': Eye,
  'eye-off': EyeOff,
  'edit': Edit,
  'trash-2': Trash2,
  'download': Download,
  'upload': Upload,
  'share': Share,
  'copy': Copy,
  'save': Save,
  'printer': Printer,
  'file': File,
  'folder': Folder,
  'image': Image,
  'video': Video,
  'music': Music,
  'code': Code,
  'camera': Camera,
  'mic': Mic,
  'play': Play,
  'pause': Pause,
  'stop': Square,
  'volume': Volume,
  'wifi': Wifi,
  'battery': Battery,
  'globe': Globe,
  'link': Link,
  'zap': Zap,
  'flame': Flame,
  'sun': Sun,
  'moon': Moon,
  'cloud': Cloud,
  'umbrella': Umbrella,
  'snowflake': Snowflake,
  'car': Car,
  'plane': Plane,
  'ship': Ship,
  'train': Train,
  'bike': Bike,
  'bus': Bus,
  'restaurant': ChefHat,
  'coffee': Coffee,
  'pizza': Pizza,
  'utensils': Utensils,
  'shopping-cart': ShoppingCart,
  'package': Package,
  'gift': Gift,
  'credit-card': CreditCard,
  'building': Building,
  'hospital': Hospital,
  'school': School,
  'factory': Factory,
  'book': Book,
  'graduation-cap': GraduationCap,
  'briefcase': Briefcase,
  'award': Award,
  'users': Users,
  'user-plus': UserPlus,
  'user-minus': UserMinus,
  'user-check': UserCheck,
  'message-square': MessageSquare,
  'message-circle': MessageCircle,
  'send': Send,
  'inbox': Inbox,
  'graph': BarChart3,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'bar-chart': BarChart,
  'pie-chart': PieChart,
  'activity': Activity,
  'target': Target,
  'layers': Layers,
  'grid': Grid,
  'list': List,
  'filter': Filter,
  'sort': ArrowUpDown,
  'refresh': RefreshCw,
  'maximize': Maximize,
  'minimize': Minimize,
  'rotate-cw': RotateCw,
  'rotate-ccw': RotateCcw,
  'info': Info,
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'help-circle': HelpCircle,
  'lightbulb': Lightbulb,
} as const;

type IconName = keyof typeof AVAILABLE_ICONS;

interface SimpleIconPickerProps {
  value?: string;
  onValueChange?: (value: string) => void;
  searchPlaceholder?: string;
  triggerPlaceholder?: string;
  maxDisplayedIcons?: number;
  minimal?: boolean;
  className?: string;
}

// Icon component wrapper
const IconComponent = React.memo(({ iconName }: { iconName: IconName }) => {
  const Icon = AVAILABLE_ICONS[iconName];
  return Icon ? <Icon className="h-4 w-4" /> : null;
});

IconComponent.displayName = "IconComponent";

export const SimpleIconPicker = React.forwardRef<
  HTMLDivElement,
  SimpleIconPickerProps
>(({
  value,
  onValueChange,
  searchPlaceholder = "Search icons...",
  triggerPlaceholder = "Select an icon",
  maxDisplayedIcons = 50,
  minimal = false,
  className,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [displayedIconsCount, setDisplayedIconsCount] = useState(maxDisplayedIcons);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Get all available icons
  const allIcons = useMemo(() => {
    return Object.keys(AVAILABLE_ICONS) as IconName[];
  }, []);

  const filteredIcons = useMemo(() => {
    if (!search.trim()) {
      return allIcons.slice(0, displayedIconsCount);
    }
    
    const searchTerm = search.toLowerCase();
    const filtered = allIcons.filter(iconName => 
      iconName.toLowerCase().includes(searchTerm)
    );
    
    return filtered.slice(0, displayedIconsCount);
  }, [search, allIcons, displayedIconsCount]);

  const handleIconSelect = (iconName: string) => {
    onValueChange?.(iconName);
    setIsOpen(false);
    setSearch("");
    setDisplayedIconsCount(maxDisplayedIcons);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const loadMoreIcons = () => {
    setDisplayedIconsCount(prev => Math.min(prev + 20, allIcons.length));
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setDisplayedIconsCount(maxDisplayedIcons);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, maxDisplayedIcons]);

  // Reset displayed count when search changes
  useEffect(() => {
    setDisplayedIconsCount(maxDisplayedIcons);
  }, [search, maxDisplayedIcons]);

  const renderIcon = (iconName: IconName) => {
    return (
      <button
        key={iconName}
        type="button"
        className={cn(
          "h-10 w-10 p-2 hover:bg-accent hover:text-accent-foreground rounded-md",
          "flex items-center justify-center border border-transparent transition-colors",
          value === iconName && "border-primary bg-primary/10"
        )}
        onClick={() => handleIconSelect(iconName)}
        title={iconName}
      >
        <IconComponent iconName={iconName} />
      </button>
    );
  };

  const getCurrentIcon = () => {
    if (!value || !(value in AVAILABLE_ICONS)) return null;
    return <IconComponent iconName={value as IconName} />;
  };

  const hasMoreIcons = search.trim() 
    ? allIcons.filter(icon => icon.toLowerCase().includes(search.toLowerCase())).length > displayedIconsCount
    : allIcons.length > displayedIconsCount;

  // If minimal mode is enabled, just show the icon
  if (minimal) {
    return (
      <div 
        ref={ref} 
        className={cn(
          "flex items-center justify-center", 
          className
        )}
        title={value}
      >
        {getCurrentIcon()}
      </div>
    );
  }

  return (
    <div ref={ref} className={cn("relative w-full", className)}>
      <Button 
        type="button"
        variant="outline" 
        className="w-full justify-between"
        onClick={handleToggle}
      >
        <div className="flex items-center">
          {value ? (
            <div className="flex items-center">
              <div className="mr-2">
                {getCurrentIcon()}
              </div>
              {value}
            </div>
          ) : (
            triggerPlaceholder
          )}
        </div>
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </Button>
      
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-2 bg-popover border rounded-md shadow-lg p-3"
        >
          <div className="space-y-3">
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
              autoFocus
            />
            
            <div className="max-h-60 overflow-y-auto">
              <div className="grid grid-cols-8 gap-1">
                {filteredIcons.map(renderIcon)}
              </div>
              
              {hasMoreIcons && (
                <div className="flex justify-center mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={loadMoreIcons}
                    className="text-xs"
                  >
                    Load More Icons ({allIcons.length - displayedIconsCount} remaining)
                  </Button>
                </div>
              )}
              
              {filteredIcons.length === 0 && (
                <div className="text-center text-muted-foreground py-6">
                  <p>No icons found for "{search}"</p>
                  <p className="text-xs mt-1">Try searching for another icon name</p>
                </div>
              )}
              
              {!search && (
                <div className="text-center text-xs text-muted-foreground mt-2 p-2 bg-muted/30 rounded">
                  <span className="font-medium">{allIcons.length} icons available</span><br />
                  Showing {Math.min(displayedIconsCount, allIcons.length)} • Search to find specific icons
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

SimpleIconPicker.displayName = "SimpleIconPicker";